import { useMemo } from 'react';
import { useGetStudyReportsQuery } from '@/state/slices/reports';
import { useFlag } from '@/hooks/useFlag';

/** A phase the scrubber shows. MS is not shown until the AI emits it. */
export type ShownPhase = 'ED' | 'ES';

export interface PhaseKeyframe {
  /** 0-based frame (scrubber step). */
  frame: number;
  phase: ShownPhase;
}

const NONE: PhaseKeyframe[] = [];

/** The dicom's ED/ES keyframes, read from the report payload the page
 *  already subscribes to (`aiPhasePredictions`, 1-based frames). Empty while
 *  the `phaseIndicators` flag is off and for single-frame dicoms (AP-909). */
export function usePhaseKeyframes(
  studyUuid: string | undefined,
  dicomUuid: string | undefined,
  frameCount: number
): PhaseKeyframe[] {
  const enabled = useFlag('phaseIndicators');
  const { data: reportsPagination } = useGetStudyReportsQuery(studyUuid || '', {
    skip: !studyUuid,
  });

  return useMemo(() => {
    const predictions = reportsPagination?.results?.[0]?.aiPhasePredictions;
    if (!enabled || !dicomUuid || !predictions || frameCount <= 1) return NONE;
    return predictions
      .filter(
        (p): p is typeof p & { phase: ShownPhase } =>
          p.dicomUuid === dicomUuid &&
          (p.phase === 'ED' || p.phase === 'ES') &&
          p.frameNumber >= 1 &&
          p.frameNumber <= frameCount
      )
      .map((p) => ({ frame: p.frameNumber - 1, phase: p.phase }))
      .sort((a, b) => a.frame - b.frame);
  }, [enabled, reportsPagination, dicomUuid, frameCount]);
}
